import { DatePicker, Flex, Segmented, Select } from 'antd'
import { Dispatch, SetStateAction } from 'react'
import { useParams } from 'react-router-dom'

import { Text } from 'src/components'
import { dayMonthYearFormatWithDot } from 'src/libs'
import { useLocales } from 'src/locales'

export type TbFilter = 'all' | 'tb' | 'nonTb'

type Props = {
  newBornsIds: (string | null | undefined)[]
  showPregnantOnly?: boolean
  setFilters: Dispatch<
    SetStateAction<
      Record<string, string | undefined> | undefined | { personIds: string[] }
    >
  >
  onChange: () => void
  tbFilter: TbFilter
  setTbFilter: (value: TbFilter) => void
}

const { RangePicker } = DatePicker

export const PersonInvestigationsFilters = ({
  newBornsIds,
  showPregnantOnly,
  setFilters,
  onChange,
  tbFilter,
  setTbFilter
}: Props) => {
  const { t } = useLocales()

  const { id } = useParams()

  const onFilterChange = (name: string, val?: string) => {
    setFilters((prev) => ({ ...prev, [name]: val ? val : undefined }))
    onChange()
  }

  const onPregnancyTypeChange = (value: string) => {
    if (value === '') {
      setFilters(() => ({
        isPregnancyMonitoring: true,
        isNewborn: true,
        personIds: [String(id), String(newBornsIds)]
      }))
      return
    }
    if (value === 'pregnancy') {
      setFilters(() => ({
        isPregnancyMonitoring: true,
        isNewborn: false,
        personIds: [String(id)]
      }))
    } else if (value === 'newborn') {
      setFilters(() => ({
        isPregnancyMonitoring: false,
        isNewborn: true,
        personIds: [String(newBornsIds)]
      }))
    }
    onChange()
  }

  return (
    <Flex wrap="wrap" gap={16} align="flex-end">
      <Flex vertical gap={8}>
        <Text>{t('tests_results.type')}</Text>
        <Segmented<TbFilter>
          value={tbFilter}
          onChange={setTbFilter}
          options={[
            { label: t('common.all'), value: 'all' },
            { label: t('organizational_structure.lab'), value: 'nonTb' },
            { label: t('consultations.tb_investigations'), value: 'tb' }
          ]}
        />
      </Flex>

      <Flex vertical gap={8}>
        <Text>{t('investigations.created_date')}</Text>
        <RangePicker
          format={dayMonthYearFormatWithDot}
          onChange={(_, dateStrings) => {
            onFilterChange('createdAtFrom', dateStrings?.[0])
            onFilterChange('createdAtTo', dateStrings?.[1])
          }}
        />
      </Flex>

      <Flex vertical gap={8}>
        <Text>{t('tests_results.result_date')}</Text>
        <RangePicker
          format={dayMonthYearFormatWithDot}
          onChange={(_, dateStrings) => {
            onFilterChange('resultDateFrom', dateStrings?.[0])
            onFilterChange('resultDateTo', dateStrings?.[1])
          }}
        />
      </Flex>

      {showPregnantOnly && (
        <Flex vertical gap={8}>
          <Text>{t('pregnancy.monitoring_period')}</Text>
          <Select
            defaultValue=""
            options={[
              {
                label: t('common.default'),
                value: ''
              },
              {
                label: t('pregnancy.isPregnancyMonitoring'),
                value: 'pregnancy'
              },
              { label: t('pregnancy.isNewborn'), value: 'newborn' }
            ]}
            onChange={onPregnancyTypeChange}
          />
        </Flex>
      )}
    </Flex>
  )
}
